import 'bootstrap/dist/css/bootstrap.min.css';
import "react-datepicker/dist/react-datepicker.css";
import 'react-toastify/dist/ReactToastify.css';

import React, {useCallback, useState} from 'react';
import { Button, Col, Row } from "reactstrap"

import PromotionHeaderForm from './PromotionHeaderForm';
import PromotionProductForm from './PromotionProductForm';
import { useGetPromotionQuery } from '../../backoffice/apis/promotionAPI';
import { CreateNewPromotionProduct, PromotionProduct } from '../../backoffice/dtos/promotions/promotionProduct';
import { getCanEdit, user } from "../../backoffice/dtos/authentication/user";
import { getUser } from '../../helpers/localStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCopy, faGear, faX } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Promotion } from '../../backoffice/dtos/promotions/promotion';
import { ErrorComponent } from '../views/ErrorComponent';
import { getMessageFromRtkError } from '../../backoffice/apis/baseAPI';
import { Loading } from '../views/Loading';
import { useGetGroupsQuery } from '../../backoffice/apis/groupsAPI';
import PromotionInfoComponent from './PromotionInfoComponent';

export default function PromotionComponent({
    promotionId,
    toggle,
    toggleCopyModal,
    isOpen,
    hidePromotionControls = false
}: {
    promotionId: number,
    toggle: () => void,
    toggleCopyModal: () => void,
    isOpen: boolean,
    hidePromotionControls ?: boolean
}) {
    const user = getUser();
    const {t} = useTranslation(['promotion_timeline','common','promotion'])
    const [ activePane, setActivePane ] = useState<"promotion" | "promotion_info">("promotion")
    const { data: promotion, isFetching: isFetchingPromotion, error } =
        useGetPromotionQuery(promotionId, { skip: !isOpen })

    const headerKey = `promotion-header-${promotionId}`

    let ui: JSX.Element;

    if (isFetchingPromotion){
        ui = (
            <Loading />
        )
    } else if (error) {
        ui = (
            <ErrorComponent error={getMessageFromRtkError(error)}/>
        );
    } else if (!promotion) {
        ui = (
            <div>
                {t('no_promotion_found_error')}
            </div>
        )
    } else if (activePane === "promotion") {
        ui = (
            <PromotionInnerComponent
                headerKey={headerKey}
                user={user}
                promotion={promotion}
                toggle={toggle}
                />
        );
    } else if (activePane === "promotion_info") {
        ui = (
            <PromotionInfoComponent
                headerKey={headerKey}
                user={user}
                promotion={promotion}
                promotionInfo={promotion.promotionInfo!}
                toggle={toggle}
                />
        )
    } else {
        ui = (
            <ErrorComponent error="Invalid state"/>
        );
    }

    return (
        <React.Suspense fallback={<Loading/>}>
            <Row>
                <Col >
                {
                    activePane !== "promotion"
                        && (
                            <Button
                                color='link'
                                onClick={() => setActivePane("promotion")}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Button>
                        )
                }
                    
                </Col>
                <Col sm="auto" className='d-flex flex-column justify-content-end'>
                    <h5> {t('promotion_creation_title')}</h5>
                </Col>
                <Col className='justify-content-end d-flex flex-row'>
                    {
                        activePane !== "promotion_info"
                            && promotion?.promotionInfo
                                && (
                                    <Button
                                        color='link'
                                        onClick={() => setActivePane("promotion_info")}
                                    >
                                        <FontAwesomeIcon icon={faGear} />
                                    </Button>
                                )
                    }
                    <Button color='link' onClick={toggleCopyModal}><FontAwesomeIcon icon={faCopy}/></Button>
                    <Button color='link' onClick={toggle}><FontAwesomeIcon icon={faX}/></Button>
                </Col>
            </Row>
            <hr />
            {ui}
        </React.Suspense>
    );
}

function PromotionInnerComponent({
    headerKey,
    user,
    promotion,
    toggle,
}: {
    headerKey: string
    user: user | undefined
    promotion: Promotion
    toggle: () => void
}) {
    const {data: groups, isFetching: groupsAreFetching, error: groupsError} = useGetGroupsQuery({ groupIds: [promotion.groupId], includeDetails: true }, { refetchOnMountOrArgChange: true })
    const [newProductState, setNewProductState] = useState<PromotionProduct | null>(null)
    const [activeTab, setActiveTab] = useState(0);

    const generateNewProduct = useCallback(() => {
        if (promotion && groups && groups.length > 0 && !groupsAreFetching){
            setNewProductState(CreateNewPromotionProduct(groups[0].measurements!, promotion.id))
        }
    }, [setNewProductState, promotion, groups, groupsAreFetching])

    const {t} = useTranslation(['promotion_timeline','common','promotion'])
    const headerForm =
        <PromotionHeaderForm user={user} key={headerKey} promotion={promotion!} isLoading={false} toggle={toggle} hidePromotionControls={false} />

    const products = promotion.promotionProducts.map(pp => (
        <PromotionProductForm
            groupId={promotion.groupId}
            user={user}
            key={pp.id}
            promotionProduct={pp}
            promotionStatus={promotion.approvalStatus}
            setNewProduct={setNewProductState} />
    ))

    let newProduct = <></>
    if (user && getCanEdit(user) && newProductState !== null && promotion) {
        newProduct = <PromotionProductForm
            groupId={promotion.groupId}
            user={user}
            key={-1}
            promotionProduct={newProductState}
            promotionStatus={promotion.approvalStatus}
            setNewProduct={setNewProductState}
        />
    } else if (user && getCanEdit(user)) {
        newProduct = <Row className="p-2">
            <Col lg={8}/>
            <Col lg={4}>
                <Button className="w-100" onClick={generateNewProduct}>
                {t('add_product_button_text')}
                </Button>
            </Col>
        </Row>
    }

    return (
        <>
            {headerForm}
            <hr />
            <h5>{t('products')}</h5>
            <hr />
            {products}

            {newProduct}
        </>
    )
}
